import {
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	useTheme,
} from "@mui/material";
import {
	MoreVerticalIcon,
	PencilIcon,
	RefreshCwIcon,
	Trash2Icon,
} from "lucide-react";
import { type MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDeletePortfolioMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePortfolioMutation";
import { useDeletePropertyMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePropertyMutation";
import {
	useIsViewer,
	usePortfolioRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type PortfolioMenuTarget,
	resolveEditCopy,
	resolvePortfolioMenuActions,
} from "web_myplann/Portfolio/Helpers/portfolioDetailMenuActions";
import { usePortfolioAdvisorRequest } from "web_myplann/Portfolio/Hooks/usePortfolioAdvisorRequest";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { DeleteItemDrawer } from "web_myplann/Shared/Components/DeleteItemDrawer";
import { useCloseOnBackPress } from "web_myplann/Shared/Hooks/useCloseOnBackPress";

type PortfolioDetailMenuProps = {
	target: PortfolioMenuTarget;
	title: string;
	allowEditByClient: boolean;
	contractNumber?: string | null;
	onUpdateValue?: () => void;
};

export const PortfolioDetailMenu = ({
	target,
	title,
	allowEditByClient,
	contractNumber,
	onUpdateValue,
}: PortfolioDetailMenuProps) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const open = anchorEl !== null;

	const { mutate: deleteProperty, isPending: isDeletingProperty } =
		useDeletePropertyMutation();
	const { mutate: deletePortfolio, isPending: isDeletingPortfolio } =
		useDeletePortfolioMutation();

	const isPending = isDeletingProperty || isDeletingPortfolio;

	const { hasRight: hasPortfolioRight } = usePortfolioRightOptional();
	const isViewer = useIsViewer();

	const { editPath, showRequestEdit, showUpdateValue, showDelete } =
		resolvePortfolioMenuActions(target, {
			allowEditByClient,
			hasUpdateValue: onUpdateValue !== undefined,
		});

	const { requestEdit } = usePortfolioAdvisorRequest({
		target,
		name: title,
		contractNumber,
	});

	const showEdit = editPath !== undefined || showRequestEdit;
	const hasAnyAction = showEdit || showUpdateValue || showDelete;

	const deleteCopy =
		target.kind === PortfolioItemKind.Property
			? Resources.Portfolio.Actions.DeleteProperty
			: Resources.Portfolio.Actions.DeletePortfolio;

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};
	useCloseOnBackPress(open, handleClose);

	const handleEdit = () => {
		handleClose();
		if (showRequestEdit) {
			requestEdit();
			return;
		}
		if (editPath) {
			navigate(editPath);
		}
	};

	const handleUpdateValue = () => {
		handleClose();
		onUpdateValue?.();
	};

	const handleDelete = () => {
		handleClose();
		setDeleteOpen(true);
	};

	const handleDeleteSuccess = () => {
		setDeleteOpen(false);
		toast.success(t(deleteCopy.successToast));
		navigate(Routing.Portfolio.PortfolioList.path());
	};

	const handleConfirmDelete = () => {
		if (target.kind === PortfolioItemKind.Property) {
			deleteProperty(target.id, { onSuccess: handleDeleteSuccess });
		} else {
			deletePortfolio(target.id, { onSuccess: handleDeleteSuccess });
		}
	};

	if (!hasPortfolioRight || isViewer || !hasAnyAction) {
		return null;
	}

	return (
		<>
			<IconButton onClick={handleOpen} size="small">
				<MoreVerticalIcon
					size={theme.iconSize.md}
					color={theme.palette.grey[600]}
				/>
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				redesign
			>
				{showUpdateValue && (
					<MenuItem key="updateValue" onClick={handleUpdateValue}>
						<ListItemIcon>
							<RefreshCwIcon size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>
							{t(Resources.Portfolio.Actions.updateValue)}
						</ListItemText>
					</MenuItem>
				)}
				{showEdit && (
					<MenuItem key="edit" onClick={handleEdit}>
						<ListItemIcon>
							<PencilIcon size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>
							{t(resolveEditCopy(target, showRequestEdit))}
						</ListItemText>
					</MenuItem>
				)}
				{showDelete && <Divider key="divider" />}
				{showDelete && (
					<MenuItem key="delete" onClick={handleDelete}>
						<ListItemIcon>
							<Trash2Icon
								size={theme.iconSize.sm}
								color={theme.palette.error.main}
							/>
						</ListItemIcon>
						<ListItemText
							slotProps={{ primary: { color: theme.palette.error.main } }}
						>
							{t(Resources.Portfolio.Actions.deleteContract)}
						</ListItemText>
					</MenuItem>
				)}
			</Menu>
			<DeleteItemDrawer
				open={deleteOpen}
				itemTitle={title}
				namespace={deleteCopy}
				isPending={isPending}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};
